import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';

interface PendingGoal {
  goal_id: string;
  employee_id: string;
  employee_name: string;
  target_description: string;
  achievement_criteria: string;
  approval_status: string;
  evaluation_status?: string | null;
  submitted_at: string;
  revision_phase: string;
  version: number;
  approval_progress?: ApprovalProgress;
  my_approval_status?: string | null;
}

interface ApprovalProgress {
  total: number;
  approved: number;
  rejected: number;
  allApproved: boolean;
}

interface GoalApproval {
  approval_id: string;
  approver_id: string;
  approver_name: string;
  evaluation_category: string;
  status: string;
  acted_at: string | null;
  comment: string | null;
  is_proxy: boolean;
  proxy_by_name: string | null;
}

interface GoalItem {
  item_detail_id: string;
  item_number: string;
  item_description: string;
  weight: number;
  requires_goal: boolean;
  is_free_item: boolean;
  target_description: string | null;
  achievement_criteria: string | null;
}

interface GoalCategory {
  category_number: number;
  category_name: string;
  total_weight: number;
  items: GoalItem[];
}

const GoalApprovalPage: React.FC = () => {
  const { periodStatus, user } = useAuth();
  const periodId = periodStatus?.activePeriodId || '';
  const isHr = user?.role === 'hr';
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejecting, setRejecting] = useState<PendingGoal | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailGoal, setDetailGoal] = useState<PendingGoal | null>(null);
  // HR代行承認: 誰の代わりに承認するか
  const [proxyApproverId, setProxyApproverId] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['pending-goals', periodId],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: { items: PendingGoal[]; canApproveGoals: boolean } }>(
        '/evaluation-goals/approval/pending',
        { params: { period_id: periodId } }
      );
      return res.data.data;
    },
    enabled: !!periodId,
  });

  const approveMutation = useMutation({
    mutationFn: ({ goalId, onBehalfOf }: { goalId: string; onBehalfOf?: string }) =>
      api.post(`/evaluation-goals/${goalId}/approve`, onBehalfOf ? { on_behalf_of: onBehalfOf } : {}),
    onSuccess: (response) => {
      toast.success(response.data.message || '目標を承認しました');
      queryClient.invalidateQueries({ queryKey: ['pending-goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal-items'] });
      // 全員の承認が揃った場合のみ閉じる
      if (response.data.data?.all_approved !== false) {
        setDetailOpen(false);
      }
      setProxyApproverId('');
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e, '承認に失敗しました')),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ goalId, reason }: { goalId: string; reason: string }) =>
      api.post(`/evaluation-goals/${goalId}/reject`, { rejection_reason: reason }),
    onSuccess: () => {
      toast.success('目標を差し戻しました');
      setRejectOpen(false);
      setRejecting(null);
      setRejectReason('');
      setDetailOpen(false);
      queryClient.invalidateQueries({ queryKey: ['pending-goals'] });
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e, '差し戻しに失敗しました')),
  });

  // 項目別の目標（詳細ダイアログで表示）
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['goal-items', detailGoal?.goal_id],
    queryFn: async () => {
      const res = await api.get<{
        success: boolean;
        data: {
          categories: GoalCategory[];
          total_items: number;
          total_weight: number;
          requires_goal_items: number;
          approvals: GoalApproval[];
          approval_progress: ApprovalProgress;
          proxy_candidates: { approver_id: string; approver_name: string }[];
        };
      }>(`/evaluation-goals/${detailGoal!.goal_id}/items`);
      return res.data.data;
    },
    enabled: detailOpen && !!detailGoal?.goal_id,
  });

  const canApprove = data?.canApproveGoals ?? false;
  const items = data?.items ?? [];

  const openDetail = (row: PendingGoal) => {
    setDetailGoal(row);
    setDetailOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">目標承認</Typography>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()}>
          更新
        </Button>
      </Box>

      {!canApprove && (
        <Alert severity="info" sx={{ mb: 2 }}>
          目標設定期間または中期レビュー期間内のみ、承認・差し戻しが可能です。
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Alert severity="success">承認待ちの目標はありません。</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>社員</TableCell>
                <TableCell>フェーズ</TableCell>
                <TableCell>承認の進捗</TableCell>
                <TableCell>目標（抜粋）</TableCell>
                <TableCell>達成基準（抜粋）</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => {
                const isApproved = row.approval_status === 'approved';
                return (
                <TableRow key={row.goal_id}>
                  <TableCell>
                    {row.employee_name}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {row.employee_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={isApproved ? 'success' : 'warning'}
                      label={isApproved ? '承認済み（差し戻し可）' : '承認待ち'}
                    />
                    <Typography variant="caption" display="block">
                      {row.revision_phase === 'mid_term' ? '中期改訂' : '期初'} v{row.version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.approval_progress && row.approval_progress.total > 0 ? (
                      <>
                        <Chip
                          size="small"
                          color={row.approval_progress.allApproved ? 'success' : 'default'}
                          label={`${row.approval_progress.approved} / ${row.approval_progress.total} 名`}
                        />
                        {row.my_approval_status === 'approved' && (
                          <Typography variant="caption" display="block" color="success.main">
                            自分は承認済み
                          </Typography>
                        )}
                        {row.my_approval_status === 'pending' && (
                          <Typography variant="caption" display="block" color="warning.main">
                            自分は未承認
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Typography variant="body2" noWrap title={row.target_description}>
                      {row.target_description}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Typography variant="body2" noWrap title={row.achievement_criteria}>
                      {row.achievement_criteria}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => openDetail(row)}
                      sx={{ mr: 1, mb: 0.5 }}
                    >
                      詳細
                    </Button>
                    {!isApproved && (
                      <Button
                        size="small"
                        color="success"
                        startIcon={<ApproveIcon />}
                        disabled={!canApprove || approveMutation.isPending}
                        onClick={() => approveMutation.mutate({ goalId: row.goal_id })}
                        sx={{ mr: 1, mb: 0.5 }}
                      >
                        承認
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<RejectIcon />}
                      disabled={!canApprove}
                      onClick={() => {
                        setRejecting(row);
                        setRejectOpen(true);
                      }}
                      sx={{ mb: 0.5 }}
                    >
                      差し戻し
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          目標の詳細 — {detailGoal?.employee_name}（{detailGoal?.employee_id}）
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (detailData?.categories?.length ?? 0) > 0 ? (
            <>
              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  評価項目 {detailData!.total_items} 件
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  配点合計 {detailData!.total_weight} 点
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  入力必須 {detailData!.requires_goal_items} 件
                </Typography>
              </Box>

              {(detailData?.approvals?.length ?? 0) > 0 && (
                <Alert
                  severity={detailData!.approval_progress.allApproved ? 'success' : 'info'}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body2" gutterBottom>
                    <strong>
                      承認の進捗: {detailData!.approval_progress.approved} / {detailData!.approval_progress.total} 名
                    </strong>
                    {!detailData!.approval_progress.allApproved &&
                      '（全員の承認が揃うと目標が確定します）'}
                  </Typography>
                  {detailData!.approvals.map((approval) => (
                    <Typography key={approval.approval_id} variant="caption" display="block">
                      {approval.approver_name}
                      （{approval.evaluation_category === 'manager' ? '上長' : '部門長'}）:{' '}
                      {approval.status === 'approved'
                        ? '承認済み'
                        : approval.status === 'rejected'
                          ? '差し戻し'
                          : '未承認'}
                      {approval.is_proxy && approval.proxy_by_name
                        ? `（${approval.proxy_by_name} が代行）`
                        : ''}
                      {approval.comment ? ` — ${approval.comment}` : ''}
                    </Typography>
                  ))}
                </Alert>
              )}

              {detailData!.categories.map((category) => (
                <Box key={category.category_number} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      観点{category.category_number}: {category.category_name}
                    </Typography>
                    <Chip label={`${category.total_weight}点`} size="small" color="primary" />
                  </Box>

                  {category.items.map((item) => {
                    const hasContent =
                      (item.target_description?.trim() ?? '') !== '' ||
                      (item.achievement_criteria?.trim() ?? '') !== '';
                    return (
                      <Card key={item.item_detail_id} variant="outlined" sx={{ mb: 1.5 }}>
                        <CardContent>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.item_number}. {item.item_description}
                            </Typography>
                            <Chip label={`${item.weight}点`} size="small" />
                            {item.is_free_item && (
                              <Chip label="フリー項目" size="small" color="info" />
                            )}
                            {item.requires_goal && (
                              <Chip label="入力必須" size="small" color="warning" />
                            )}
                          </Box>

                          {hasContent ? (
                            <>
                              <Typography variant="caption" color="text.secondary">
                                目標設定（SMART）
                              </Typography>
                              <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                                {item.target_description || '（未入力）'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                達成基準
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {item.achievement_criteria || '（未入力）'}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              （目標の入力はありません）
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ))}
            </>
          ) : (
            detailGoal && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  評価項目が生成されていないため、従来の単一目標を表示しています。
                </Alert>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  目標設定（SMART）
                </Typography>
                <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                  {detailGoal.target_description}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  達成基準
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {detailGoal.achievement_criteria}
                </Typography>
              </>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {isHr && (detailData?.proxy_candidates?.length ?? 0) > 0 && (
            <FormControl size="small" sx={{ minWidth: 220, mr: 'auto' }}>
              <InputLabel id="proxy-approver-label">代行して承認する承認者</InputLabel>
              <Select
                labelId="proxy-approver-label"
                label="代行して承認する承認者"
                value={proxyApproverId}
                onChange={(e) => setProxyApproverId(e.target.value)}
              >
                <MenuItem value="">（代行しない）</MenuItem>
                {detailData!.proxy_candidates.map((candidate) => (
                  <MenuItem key={candidate.approver_id} value={candidate.approver_id}>
                    {candidate.approver_name} の代行
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button onClick={() => setDetailOpen(false)}>閉じる</Button>
          {detailGoal && (
            <>
              {detailGoal.approval_status !== 'approved' && (
                <Button
                  color="success"
                  variant="contained"
                  disabled={!canApprove || approveMutation.isPending}
                  onClick={() =>
                    approveMutation.mutate({
                      goalId: detailGoal.goal_id,
                      onBehalfOf: proxyApproverId || undefined,
                    })
                  }
                >
                  {proxyApproverId ? '代行して承認' : '承認'}
                </Button>
              )}
              <Button
                color="error"
                disabled={!canApprove}
                onClick={() => {
                  setRejecting(detailGoal);
                  setRejectOpen(true);
                }}
              >
                差し戻し
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>目標の差し戻し</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {rejecting?.employee_name} さんの目標を差し戻します。
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="差し戻し理由"
            required
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>キャンセル</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || rejectMutation.isPending}
            onClick={() => {
              if (rejecting) {
                rejectMutation.mutate({ goalId: rejecting.goal_id, reason: rejectReason });
              }
            }}
          >
            差し戻す
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GoalApprovalPage;
